import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { AuthTokens } from '../AppContext';
import { acquireIdToken } from './azureAuth';
import Swal from "sweetalert2";
import jwtDecode from "jwt-decode";
import { token_config } from '../AppContext';

export const checkTimeIntervalOfAccessTokenExpiration = 300;
export const checkTimeIntervalOfRefreshTokenExpiration = 600;
export let lastServerTime: any;
let isAccessTokenGoingToExpire = false;
let initialAccessTokenExpireTimeout: any = null;

export enum AuthenticationType {
  Azure = 1,
  Form = 2,
}

export interface ErrorModel {
  ErrorCode: string;
  Message: string;
}

export enum ErroCodes {
  INVALID_USER = 'invalid_user',
  ACCOUNT_LOCKED = 'account_locked',
  SESSION_EXPIRED = 'session_expired'
}

export interface DecodedToken {
  exp: number;
  UserId: number,
  AuthenticationTypeId: string;
  Email: string;
  OfficeId: "1",
  Privileges: string;
  nbf: number;
  iss: string;
  aud: string;
}

export interface LoginPostModel {
  idToken: string | null,
  authenticationTypeId: number,
  email: string | null,
  password: string | null
}

export { AxiosError } from "axios";

export interface ErrorResponseData {
  ErrorCode: string;
  Message: string;
}

export const errorMessageForCode = (code: any) => {
  const messageArr: any = {
    400: '400 not found',
    401: '401 unauthorized',
    403: '403 forbidden',
    404: '404 page not found',
    405: '405 method not allowed',
    408: '408 request timeout',
    422: '422 unprocessable content',
    500: '500 internal server error',
    501: '501 invalid input',
    502: '502 bad gateway',
    503: '503 service unavailable',
    522: '522 connection timed out',
  }
  return messageArr[code] ? messageArr[code] : messageArr[500];
}

const envAPIURL =
  process.env.REACT_APP_APIURL !== undefined
    ? process.env.REACT_APP_APIURL
    : "";

const instance = axios.create({
  baseURL: envAPIURL,
});

let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

function subscribeTokenRefresh(cb: (token: string) => void) {
  refreshSubscribers.push(cb);
}

function onRrefreshed(token: string) {
  refreshSubscribers.map(cb => cb(token));
}

function isErrorResponseData(data: any): data is ErrorResponseData {
  return typeof data.ErrorCode === 'string' && typeof data.Message === 'string';
}

export async function getServerTime() {
  try {
    const response = await instance.get('/Auth/GetServerTimeUTC');
    if (response.data) {
      // Divided by 1000 to eliminate milli seconds
      lastServerTime = new Date(response.data).getTime() / 1000;

      const accessToken = localStorage.getItem(token_config.ACCESS_TOKEN);
      if (accessToken) {
        if (lastServerTime) {
          const decodedToken = jwtDecode<DecodedToken>(accessToken);
          const timeUntilExpiration = decodedToken.exp - lastServerTime;
          if (timeUntilExpiration > checkTimeIntervalOfAccessTokenExpiration) {
            clearTimeout(initialAccessTokenExpireTimeout);
            initialAccessTokenExpireTimeout = setTimeout(() => {
              // On page open but no activity
              isAccessTokenGoingToExpire = true;
            }, (timeUntilExpiration - checkTimeIntervalOfAccessTokenExpiration) * 1000);
          } else {
            // On page refresh
            isAccessTokenGoingToExpire = true;
          }
        }
      }
      return lastServerTime;
    }
  } catch (error) {
    console.error('Error fetching server time:', error);
  }
  return null;
}

async function getAzureAccessToken(idToken: string | null) {
  try {
    if (!idToken) { return null }

    if (!isRefreshing) {
      isRefreshing = true;

      const loginModel = {} as LoginPostModel;
      const response = await instance.post("/Auth/Login", {
        ...loginModel,
        AuthenticationTypeId: AuthenticationType.Azure, // Use the enum value here
        idToken: idToken,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      localStorage.setItem(token_config.ACCESS_TOKEN, response.data.accessToken);

      isRefreshing = false;
      isAccessTokenGoingToExpire = false;
      // restart timer to check the expiration
      await getServerTime();

      onTokenRefreshedCallback();
      onRrefreshed(response.data.accessToken);
      return response.data.accessToken;
    } else {
      return new Promise<string>((resolve) => {
        subscribeTokenRefresh(resolve);
      });
    }
  } catch (error) {
    console.error('Error fetching azure refresh token:', error);
  }
  return null;
}

let tokenRefreshedCallback: any = null;

function onTokenRefreshedCallback() {
  // Notify the callback that the token has been refreshed
  if (typeof tokenRefreshedCallback === 'function') {
    tokenRefreshedCallback();
  }
}

export function setTokenRefreshedCallback(callback: () => void) {
  // Set the callback function
  tokenRefreshedCallback = callback;
}

async function getRefreshToken(expiredAccessToken: string, refreshToken: string | null) {
  try {
    if (!isRefreshing) {
      isRefreshing = true;

      const response = await instance.post('/Auth/Refresh', { expiredAccessToken, refreshToken });
      localStorage.setItem(token_config.ACCESS_TOKEN, response.data.accessToken);
      localStorage.setItem(token_config.REFRESH_TOKEN, response.data.refreshToken);

      isRefreshing = false;
      isAccessTokenGoingToExpire = false;
      // restart timer to check the expiration
      await getServerTime();

      onTokenRefreshedCallback();
      onRrefreshed(response.data.accessToken);
      return response.data.accessToken;
    } else {
      return new Promise<string>((resolve) => {
        subscribeTokenRefresh(resolve);
      });
    }
  } catch (error: unknown) {
    const castError = error as ErrorModel;
    if (castError.ErrorCode === ErroCodes.SESSION_EXPIRED) {
      console.error('Error refreshing access token:', error);
    }
    return null;
  }
}


async function checkAndRefreshToken(accessToken: string, refreshToken: string | null, decodedToken: DecodedToken | undefined) {
  try {
    // Check access token going to expire
    if (decodedToken && isAccessTokenGoingToExpire) {
      if (parseInt(decodedToken.AuthenticationTypeId) === AuthenticationType.Form) {
        return await getRefreshToken(accessToken, refreshToken);
      } else if (parseInt(decodedToken.AuthenticationTypeId) === AuthenticationType.Azure) {
        // Acquire a new ID token from Azure AD
        const IdToken = await acquireIdToken();
        return await getAzureAccessToken(IdToken);
      }
    }

    return accessToken;
  } catch (error) {
    console.error('Error checking and refreshing token:', error);
    return accessToken;
  }
}

instance.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    let requestConfig = config;

    try {
      if (!config.url?.toLocaleLowerCase()?.endsWith("refresh")
        && !config.url?.toLocaleLowerCase()?.endsWith("getservertimeutc")
        && !config.url?.toLocaleLowerCase()?.endsWith("login")
        && !config.url?.toLocaleLowerCase()?.endsWith("logerror")) {
        // Get the access token and refresh token from local storage
        const accessToken: any = localStorage.getItem(token_config.ACCESS_TOKEN);
        const refreshToken = localStorage.getItem(token_config.REFRESH_TOKEN);

        let decodedToken;
        let isValidToken;
        try {
          decodedToken = jwtDecode<DecodedToken>(accessToken);
          isValidToken = true;
        } catch (error) {
          isValidToken = false;
        }

        if (isValidToken) {
          const updatedAccessToken = await checkAndRefreshToken(accessToken, refreshToken, decodedToken);
          if (updatedAccessToken) {
            requestConfig.headers.Authorization = `Bearer ${updatedAccessToken}`;
          } else {
            // Handle error case when the access token cannot be refreshed
            // You can redirect the user to the login page or show an error message
            localStorage.removeItem(token_config.ACCESS_TOKEN);
            localStorage.removeItem(token_config.REFRESH_TOKEN);
            window.location.href = '/';
          }
        } else {
          // Handle error case when the access token cannot be refreshed
          // You can redirect the user to the login page or show an error message
          localStorage.removeItem(token_config.ACCESS_TOKEN);
          localStorage.removeItem(token_config.REFRESH_TOKEN);
        }
      }
      return requestConfig;
    } catch (error) {
      console.log('interceptors request error 1 >>>> ', error);
      return Promise.reject(error);
    }
  },
  (error) => {
    console.log('interceptors request error 2 >>>> ', error);
    return Promise.reject(error);
  }
);

instance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error: AxiosError) => {
    try {
      if (error.response && isErrorResponseData(error.response.data)) {
        const errorCode = error.response.data.ErrorCode;
        const errorMessage = error.response.data.Message;

        if (errorCode === "credentials_invalid" || errorCode === "account_locked") {
          // Exclude handling for 'credentials_invalid' error
          return Promise.reject(error);
        }

        switch (error.response.status) {
          case 401:
            // Handle 401 Unauthorized errors
            switch (errorCode) {
              case 'invalid_user':
                break;
              case 'account_locked':
                break;
              case 'session_expired':
                localStorage.removeItem(token_config.ACCESS_TOKEN);
                localStorage.removeItem(token_config.REFRESH_TOKEN);
                window.location.href = '/';
                break;
              default:
                break;
            }
            break;
          case 500:
            // Handle 500 Internal Server Error
            Swal.fire({
              icon: 'error',
              title: 'Ope!',
              html: `<p>${errorMessage}</p><p>(${errorMessageForCode(error.response.status)})</p>`,
              confirmButtonText: 'OK',
            });
            break;
          case 501:
            // Handle 501 Not Implemented or another custom status code
            Swal.fire({
              icon: 'error',
              title: 'Ope!',
              html: `<p>${errorMessage}</p><p>(${errorMessageForCode(error.response.status)})</p>`,
              confirmButtonText: 'OK',
            });
            break;
          case 422:
            // Handle 422 Unprocessable Content
            Swal.fire({
              icon: 'error',
              title: 'Ope!',
              html: `<p>${errorMessage}</p><p>(${errorMessageForCode(error.response.status)})</p>`,
              confirmButtonText: 'OK',
            });
            break;
          case 400:
            // Handle 400 Not Found
            Swal.fire({
              icon: 'error',
              title: 'Ope!',
              html: `<p>${errorMessage}</p><p>(${errorMessageForCode(error.response.status)})</p>`,
              confirmButtonText: 'OK',
            });
            break;
          default:
            break;
        }
      } else {
        // Handle network errors or other errors that don't have a response
        let errorMessage: any = '';
        if (typeof error?.response?.data === 'object') {
          errorMessage = (error?.response?.data as any).title;
        } else if (error?.response?.data) {
          errorMessage = error?.response?.data;
        } else if (error?.response?.status === 500) {
          errorMessage = 'Unexpected error has occurred. Notified the system admin. Please try again after some time.';
        }
        if (errorMessage) {
          Swal.fire({
            icon: 'error',
            title: 'Ope!',
            html: `<p>${errorMessage}</p><p>(${errorMessageForCode(error?.response?.status)})</p>`,
            confirmButtonText: 'OK',
          });
        }
      }
      logError(error);
    } catch (catchError) {
      console.error('Global error handler:', catchError);
      Swal.fire({
        icon: 'error',
        title: 'Ope!',
        text: 'An error occurred while processing your request.',
        confirmButtonText: 'OK',
      });
    } finally {
      return Promise.reject(error);
    }
  }
);

function logError(error: any) {
  if (!error?.config?.url?.toLocaleLowerCase()?.endsWith("logerror")) {
    let parameters = error?.config?.data ? error.config.data : '';
    if (error?.config?.url?.toLocaleLowerCase()?.endsWith("login")) {
      const postData = JSON.parse(error?.config?.data);
      delete postData?.password;
      parameters = JSON.stringify(postData);
    }
    const payload = {
      statusCode: error?.response?.status ? error.response.status : 0,
      errorMessage: error?.message ? error.message : '',
      parameters: parameters,
      url: error?.config?.url ? error.config.url : '',
      hTTPVerb: error?.config?.method ? error.config.method : ''
    };
    if (payload.errorMessage !== 'Operation canceled due to a new request.') {
      instance.post(`/Error/LogError`, payload);
    }
  }
}

export default instance;
